import { NOT_MEASURED } from '../features/explore/fleetSummary'
import type { FleetCoverage } from './api'

/**
 * The line along the bottom of every screen.
 *
 * Each segment is decided on its own and each is `—` when its own source did not arrive. That is not
 * fastidiousness: a status bar is read at a glance and believed without checking, so it is the worst
 * possible place for a number that was never measured. "214 agents connected" printed because a read
 * failed is D-263 one row further down the page than the dashboard that once said *Every machine is
 * covered* beside an expired session.
 *
 * A module rather than four lines inside the component for the reason the SPA has no component
 * tests: the decision here is which sentence to print, and that is testable without a browser while
 * the layout is not.
 */
export type SegmentTone = 'normal' | 'problem'

export interface StatusSegment {
  key: string
  text: string
  tone: SegmentTone
}

export interface StatusBarSource {
  /** Null when the read did not arrive. Zero-filled counts mean it arrived and counted nothing. */
  coverage: FleetCoverage | null
  /** What the current screen is showing, already phrased by that screen. */
  rows: string | null
  syncedUtc: Date | null
}

function plural(count: number, one: string, many: string): string {
  return `${count.toLocaleString()} ${count === 1 ? one : many}`
}

export function statusSegments(source: StatusBarSource): StatusSegment[] {
  const { coverage, rows, syncedUtc } = source

  const segments: StatusSegment[] = [
    {
      key: 'reporting',
      text:
        coverage === null
          ? `${NOT_MEASURED} devices reporting`
          : `${plural(coverage.devicesReporting, 'device', 'devices')} reporting`,
      tone: 'normal'
    }
  ]

  if (rows !== null) segments.push({ key: 'rows', text: rows, tone: 'normal' })

  // The number the whole coverage idea exists to keep on screen. It is a problem when it is a real
  // measured number above zero - never merely because the read failed, and never when it is a
  // genuine zero, which is the one case worth saying plainly rather than coloring.
  segments.push({
    key: 'unenforced',
    text:
      coverage === null
        ? `${NOT_MEASURED} carrying no policy`
        : coverage.devicesCarryingNoPolicy === 0
          ? 'every device carries a policy'
          : `${plural(coverage.devicesCarryingNoPolicy, 'device carries', 'devices carry')} no policy`,
    tone: coverage !== null && coverage.devicesCarryingNoPolicy > 0 ? 'problem' : 'normal'
  })

  if (coverage !== null && coverage.auditChainsBroken > 0) {
    segments.push({
      key: 'chains',
      text: `${plural(coverage.auditChainsBroken, 'log', 'logs')} no longer verify`,
      tone: 'problem'
    })
  }

  segments.push({
    key: 'synced',
    // Time only: the date is on every row of every table below, and a status bar that repeats it
    // spends its width saying what the page already said.
    text: syncedUtc === null ? `synced ${NOT_MEASURED}` : `synced ${syncedUtc.toLocaleTimeString()}`,
    tone: 'normal'
  })

  return segments
}
